import React from 'react';
import { toast } from 'react-toastify';
import { useGetDocumentsByLoanApplication, useGetLoanApplication } from '../../services/loanService';
import { downloadDocument } from '../../services/documentService';
import { DocumentReference } from '../../services/schemas/loanServiceSchemas/documentReference';
import { formatDocumentDate, getDocumentUploadDate, getDocumentUploader } from '../../utils/documentDates';
import DocumentActionsMenu from './DocumentActionsMenu';
import { DocumentPreviewModal, useDocumentPreview } from './DocumentPreview';

/**
 * The signed pre-agreement and loan agreement, kept with the loan they belong to.
 *
 * Both were filed on the application and reachable only from its documents, not from the loan
 * account the agreement governs. Replaced versions are listed too, marked as previous, so what the
 * customer actually signed is never lost behind a newer upload.
 *
 * The signed loan agreement carries its signature record: uploading the client-signed copy is the
 * signature, so who uploaded it and when is shown on that file.
 */

interface LoanAgreementsCardProps {
  applicationId?: string;
}

const AGREEMENT_TYPES: Record<string, { label: string; order: number }> = {
  PRE_LOAN_AGREEMENT: { label: 'Pre-agreement', order: 0 },
  LOAN_AGREEMENT: { label: 'Loan agreement', order: 1 }
};

const LoanAgreementsCard: React.FC<LoanAgreementsCardProps> = ({ applicationId }) => {
  const { data: documents, isLoading } = useGetDocumentsByLoanApplication(
    applicationId || '',
    { includeSuperseded: true },
    { query: { enabled: !!applicationId } }
  );
  const { data: application } = useGetLoanApplication(applicationId || '', { query: { enabled: !!applicationId } });
  const documentPreview = useDocumentPreview(message => toast.error(message));
  const [downloadingKey, setDownloadingKey] = React.useState<string | null>(null);

  const handleDownload = async (documentKey: string, name?: string) => {
    setDownloadingKey(documentKey);
    try {
      const blob = await downloadDocument(documentKey);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name || 'agreement';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading agreement:', error);
      toast.error('Failed to download agreement');
    } finally {
      setDownloadingKey(null);
    }
  };

  if (!applicationId || isLoading) return null;

  // Pre-agreement before loan agreement; within each, the current version first.
  const agreements: DocumentReference[] = (documents || [])
    .filter(doc => doc.documentType && AGREEMENT_TYPES[doc.documentType])
    .sort((a, b) =>
      AGREEMENT_TYPES[a.documentType!].order - AGREEMENT_TYPES[b.documentType!].order
      || Number(a.status === 'SUPERSEDED') - Number(b.status === 'SUPERSEDED')
      || String(getDocumentUploadDate(b) || '').localeCompare(String(getDocumentUploadDate(a) || ''))
    );

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center py-2">
        <h6 className="card-title mb-0">
          <i className="fa fa-file-text-o me-2"></i>
          Agreements
        </h6>
        {agreements.length > 0 && (
          <span className="badge bg-secondary rounded-pill">{agreements.length}</span>
        )}
      </div>
      {agreements.length === 0 ? (
        <div className="card-body">
          <p className="text-muted mb-0">No signed agreements are on file for this loan.</p>
        </div>
      ) : (
        <div className="list-group list-group-flush">
          {agreements.map(doc => {
            const documentKey = doc.documentKey || doc.id || '';
            const superseded = doc.status === 'SUPERSEDED';
            const uploader = getDocumentUploader(doc);
            const isSignedCopy = !!application?.agreementSignedAt
              && !!application.agreementDocumentKey
              && documentKey === application.agreementDocumentKey;
            return (
              <div
                key={doc.id || documentKey}
                className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2"
              >
                <div className="me-3">
                  <div className="fw-semibold small">
                    {AGREEMENT_TYPES[doc.documentType!].label}
                    {superseded && (
                      <span className="badge bg-light text-muted border ms-2">Previous version</span>
                    )}
                    {isSignedCopy && (
                      <span className="badge bg-success ms-2">
                        <i className="fa fa-check me-1"></i>Signed
                      </span>
                    )}
                  </div>
                  {isSignedCopy && (
                    <div className="small text-success">
                      Signed copy recorded {formatDocumentDate(application?.agreementSignedAt)}
                      {application?.agreementSignedByName && ` by ${application.agreementSignedByName}`}
                    </div>
                  )}
                  <div className="small text-muted">
                    {doc.name || 'Unnamed document'}
                    {' · '}uploaded {formatDocumentDate(getDocumentUploadDate(doc))}
                    {uploader && ` by ${uploader}`}
                  </div>
                </div>
                <DocumentActionsMenu
                  id={`agreement-${doc.id || documentKey}`}
                  actions={[
                    {
                      label: 'View',
                      icon: 'fa-eye',
                      busy: documentPreview.openingKey === documentKey,
                      onClick: () => documentPreview.open(documentKey, doc.name)
                    },
                    {
                      label: 'Download',
                      icon: 'fa-download',
                      busy: downloadingKey === documentKey,
                      onClick: () => handleDownload(documentKey, doc.name)
                    }
                  ]}
                />
              </div>
            );
          })}
        </div>
      )}

      <DocumentPreviewModal preview={documentPreview.preview} onClose={documentPreview.close} />
    </div>
  );
};

export default LoanAgreementsCard;
